import { setDefaultClientExport } from "./index.js";
import { 
    IEventEmitter,
    EventEmitter,
    ISystem,
    System,
    ENDPOINTS,
    LEAVE_AREA_REASONS,
    PROTOCOLS,
    minutesToMs,
    removeFromArray,
    ProtocolArray,
    GameGateData,
    ReceivedMessage,
    GameId,
    GameType,
    CLIENT_RECONNECT_MAX_ATTEMPTS,
    CLIENT_RECONNECT_BACKOFF_MS,
    CLIENT_RECONNECT_BACKOFF_CAP_MS
} from "@gotti/core";
import { BaseClientConnector } from "./ClientConnectors/BaseClientConnector.js";
import { ClientConnectorWebSocket } from "./ClientConnectors/ClientConnectorWebsocket.js";
import { ClientProcess, JoinedAreaData } from "./ClientProcess.js";
import { httpPostAsync } from "./utils.js";
import { IndexedDBLoader } from './IndexedDbLoader.js';
import { ClientConnectorUDP } from "./ClientConnectors/ClientConnectorUDP.js";
import { ClientConnectorWebRTC } from "./ClientConnectors/ClientConnectorWebRTC.js";

/**
 * Client configuration options.
 */
export type ClientConfig = {
    // used for unreliable messaging, when it hits this number for ack/seq codes it will reset to 0. Default is 63.
    maxAck?: number,
    useIndexedDb?: boolean,
    indexedDbName?: string,
    gateServerURL: string,
    requestTimeout: number,
    failedPeerRequestTrackerTimeout: number,
    useWebRtcConnector?: boolean,
    udpPort?: number,
    udpHost?: string,
}


export enum ClientStatus {
    NONE,
}

/**
 * Auth data returned from gate server authentication.
 */
export type AuthenticatedData = {
    publicId: string | number,
    privateId: string,
    auth: any,
}

/**
 * Basic game listing config from the gate.
 */
export type GameConfig = {
    name: string,
    region?: string,
    data?: any
}

/**
 * List of available games.
 */
export type GameList = GameConfig[];

/**
 * Connection details for a joined game session.
 */
export type JoinedGameInfo = {
  host: string,
  port: number,
  connectorType: 'udp' | 'websocket' | 'webRtc',
  gameId: GameId,
  gameType: GameType,
  gameData: any,
  /**
   * The in-game id the Gate reserved this seat under. Normally the one
   * `authenticate()` returned, but the id of a RESUMED session when the Gate
   * matched this authentication to a seat it was already holding for the same
   * verified identity (e.g. after a page reload, which mints a new privateId
   * AND publicId). `joinGame` adopts it — the connector validates the publicId
   * the client sends against the reserved seat's.
   */
  publicId?: string | number,
}

/**
 * Client-side game definition.
 *
 * @template TApi - Shape of the object returned by the `api` method.
 *   When provided, the same keys **must** be declared in
 *   `ClientApiRegistry` via `declare module '@gotti/client'`.
 */
export type ClientGameFile<TApi = {}> = {
  type: GameType,
  systems: Array<ISystem | System>,
  isNetworked: boolean,
  api?: TApi | ((data?: any, envOptions?: any) => TApi | Promise<TApi>),
  // Preferred simulation step in seconds (defaults to 1/60 if omitted)
  timeStep?: number,
  // If true, run a fixed timestep accumulator loop; otherwise variable timestep.
  fixedTimeStep?: boolean,
  /** Enable debug system for development. When true, adds the debug UI automatically. */
  debug?: boolean,
  onBeforeUpdate?: (delta: number) => void,
  onAfterUpdate?: (delta: number) => void,
  /** Client-namespace asset paths preloaded (into $assets) before this file's systems start. */
  preloads?: string[],
}

/**
 * Client-side area definition.
 *
 * @template TApi - Shape of the object returned by the `api` method.
 *   When provided, the same keys **must** be declared in
 *   `ClientApiRegistry` via `declare module '@gotti/client'`.
 */
export type ClientAreaFile<TApi = {}> = {
    type: string,
    systems: Array<ISystem | System>,
    api?: TApi | ((gameData?: any, envOptions?: any) => TApi | Promise<TApi>),
    /** Enable debug system for development. When true, adds the debug UI automatically. */
    debug?: boolean,
    onJoined?: (client: Client, data: { area: JoinedAreaData, options?: any }) => void,
    onLeft?: (client: Client, data: { areaId: string, reason: LEAVE_AREA_REASONS, options?: any }) => void,
    onUpdatedState?: (client: Client, data: { areaId: string, newState: any, previousState: any }) => void,
		onRawAreaMessage?: (client: Client, data: ArrayBuffer) => void,
    /** Connection to this area was lost; the auto-reconnect loop is starting.
     *  Fires once per joined area the moment the connector drops. Membership is
     *  NOT lost yet — the server grace-holds this client. */
    onReconnectStart?: (client: Client, data: { areaId: string }) => void,
    /** The area rebound after a reconnect (server re-pushed CLIENT_JOINED_AREA).
     *  `onJoined` deliberately does NOT re-fire — membership never lapsed. */
    onReconnectSuccess?: (client: Client, data: { area: JoinedAreaData, options?: any }) => void,
    /** The area could not be rejoined: `'area-gone'` (the server did not route
     *  us back into it — it no longer exists), `'gave-up'` (the reconnect attempt
     *  cap was exhausted), or `'rejected'`.
     *
     *  An area that HOLDS A SEAT for you cannot refuse your rejoin: the server
     *  matched your verified auth identity to that held seat and rebinds you
     *  WITHOUT ever calling the area's `onJoinRequest`. Do not write an admission
     *  hook to guard reconnects — it will never run for a held rejoin.
     *
     *  `'rejected'` is NOT produced by the automatic reconnect path at all (that
     *  path is GET_FIRST_AREAS, whose per-area outcomes are a rebind or
     *  `'area-gone'`). It comes from a client-initiated `joinArea()` — a
     *  REQUEST_JOIN_AREA the connector could not satisfy, in practice because
     *  that area is not connected to it — landing while a reconnect is still
     *  pending for the same area. */
    onReconnectFailure?: (client: Client, data: { areaId: string, reason: 'rejected' | 'area-gone' | 'gave-up' }) => void,
    //onBeforeUpdate?: (delta: number) => void,
    //onAfterUpdate?: (delta: number) => void,
    /** Client-namespace asset paths preloaded (into $assets) before this file's systems start. */
    preloads?: string[],
}

/**
 * Auth state stored on the client instance.
 */
export type GottiAuthData = { privateId: string, publicId: string | number, id: string | number, data: any } 

/**
 * Public API surface exposed via the default client export.
 */
export type PublicApi = IEventEmitter & {
    indexedDb?: IndexedDBLoader,
    clearGame: () => void,
    getGames: (requestPayload?: any) => Promise<GameGateData[]>,
    joinGame: (options: any, suppressJoinAreas?: boolean) => Promise<{
        game: JoinedGameInfo,
        joiningAreaIds?: Array<string>
    }>
    joinArea: (areaId: string, options?: any) => { area: JoinedAreaData, options?: any },
    joinInitialAreas: (options: any) => Array<string>,
    startGame?: (gameType: string | ClientGameFile, gameData?: any) => Promise<boolean>,
    authenticate: (requestPayload: any) => Promise<AuthenticatedData>,
    auth: GottiAuthData
}

const mergeDefaultConfig = (c1?: Partial<ClientConfig>) : ClientConfig => {
    const defaultConfig : ClientConfig = {
        requestTimeout: 30000,
        gateServerURL: window.location.origin,
        failedPeerRequestTrackerTimeout: minutesToMs(10),
    }
    c1 = c1 || {};
    return { ...defaultConfig, ...c1 };
}
type PeerCb<T> = (playerId: string, opts: any) => T
/**
 * Main client entry point for browser usage.
 *
 * @example
 * const client = new Client({ gateServerURL: 'http://localhost:3000' });
 */
export class Client extends EventEmitter {
  readonly config: ClientConfig;
  readonly status : ClientStatus;

  private connector?: BaseClientConnector;

  readonly peerStartedCbs: Array<PeerCb<void>> = []
  readonly leftAreaCbs : Array<(v: { areaId: string | number, reason: LEAVE_AREA_REASONS, options?: any }) => void> = [];
  readonly joinedAreaCbs: Array<(areaData: JoinedAreaData, options?: any) => void> = []
  readonly peerEndedCbs: Array<((peerId: string | number) => void)> = []
  public handleAreaRequest?: (areaId: string | number, requestData: ReceivedMessage) => any | void | Promise<void | any>;
  public handlePeerMessage?: (peerId: string | number, data: { type: string | number, data: any }) => void;
  public handlePeerConnectionRequest?: PeerCb<any>;

  readonly process : ClientProcess;
  readonly auth: GottiAuthData = { privateId: '', publicId: '', id: '', data: null };

  public forceClosed : boolean = false;

  /**
   * Bound browser page-unload listener (pagehide/beforeunload) that signals a
   * deliberate leave on reload/tab-close. Undefined under node/mock/test
   * runtimes (no `window`). Registered in the constructor, removed by
   * close()/clearGame() so a torn-down Client leaves no dangling window listener.
   */
  private unloadHandler?: () => void;

  public privateId: string;
  public publicId: string | number;

  // ── Auto-reconnect driver state ────────────────────────────────────────
  // Set on the first successful joinGame so a reconnect can go back through the
  // SAME gate with the SAME game-selection options (the gate re-picks the game
  // from onJoinGame(options) — there is no "rejoin exact gameId" HTTP contract,
  // so we must replay the original options; the gate detects the rejoin off the
  // grace-held privateId, not off anything we send). Kept as TWO separate
  // fields: callers commonly pass a game-selection body (e.g. `{ gameId }`)
  // AND a distinct initial-area shape (e.g. an areaId array) as two different
  // arguments — `lastGameJoinBody` must go back to the gate exactly as sent
  // (some gates, including the default one, throw if handed the area shape
  // instead), while `lastAreaJoinOptions` is what pingForFirstAreas replays.
  private lastGameJoinBody?: any;
  private lastAreaJoinOptions?: any;
  private joinedGameId?: GameId;
  private reconnectAttempt = 0;
  private reconnecting = false;
  // Areas whose rejoin is still outstanding for the current reconnect episode.
  private pendingRejoinAreaIds?: Set<string>;

  readonly publicApi: PublicApi;

  readonly gameFiles :{[key: string]: ClientGameFile} = {};
  readonly areaFiles :{[key: string]: ClientAreaFile} = {};

  readonly initialzingAreas : {[key: string]: boolean } = {};
  readonly onFinishedInitializingAreaListeners : {[areaId: string]: Array<() => void> } = {};

  public connectorUrl : string;
  readonly indexedDb?: IndexedDBLoader;

  constructor(config?: Partial<ClientConfig>, gameFiles?: {[key: string]: ClientGameFile }, readonly envOptions?: any) {
      super();
      // console.log('constructing client');
      this.config = mergeDefaultConfig(config);
      if(config.useIndexedDb) {
          this.indexedDb = new IndexedDBLoader(config.indexedDbName || '__gotti_engine');
          this.indexedDb.initialize();
      }
      this.process = new ClientProcess(this, this.indexedDb);
    
      this.gameFiles = gameFiles || {};

      this.publicApi = {
        indexedDb: this.indexedDb,
        clearGame: this.clearGame.bind(this),
        getGames: this.getGames.bind(this),
        joinGame: this.joinGame.bind(this),
        joinInitialAreas: this.joinInitialAreas.bind(this),
        joinArea: this.joinArea.bind(this),
        authenticate: this.authenticate.bind(this),
        auth: this.auth,
        on: this.on.bind(this),
        once: this.once.bind(this),
        emit: this.emit.bind(this),
        off: this.off.bind(this),
        removeAllListeners: this.removeAllListeners.bind(this),
      } as PublicApi;

      setDefaultClientExport(this);
      this.installUnloadHandler();
  }

  /**
   * `string | number`, not `string`: this is a getter for `publicId`, and
   * `useSequentialPublicIds` mints NUMBERS. Coerce with `String(client.id)`
   * before using it as an object/Map key.
   */
  get id() : string | number {
    return this.publicId;
  }
  set id(_: string) {
    throw new Error(`Dont set id.`)
  }

  get peers() {
    return this.connector.connectedPeerIds;
  }

  public sendPeer(playerId: string | number, data: { type: string | number, data: any }) {
    this.connector.sendClientPeerMessage(playerId, data);
  }
  
  /**
   * Send a message to specific peers.
   */
  public sendPeers(playerIds: (string | number)[], data: { type: string | number, data: any }) {
    this.connector.sendClientPeersMessage(playerIds, data);
  }
  /**
   * Broadcast a message to all peers.
   */
  public sendAllPeers(data: { type: string | number, data: any }) {
    this.connector.sendClientAllPeersMessage(data);
  }

  /**
   * Emit a custom game event to client listeners.
   */
  public emitGameEvents(eventName: string | number, eventData: any) {
    this.emit(`__game_event_${eventName}`, eventData)
  }
  
  /**
   * Listen to a game event (area message alias).
   */
  public onAreaMessage(eventName: string | number, cb: (eventData: any) => any) {
      this.onGameEvent(eventName, cb);
  }
  /**
   * Stop listening to a game event (area message alias).
   */
  public offAreaMessage(eventName: string | number, cb: (eventData: any) => any) {
      this.offGameEvent(eventName, cb);
  }
  /**
   * Listen once to a game event (area message alias).
   */
  public onceAreaMessage(eventName: string | number, cb: (eventData: any) => any) {
      this.onceGameEvent(eventName, cb);
  }
  /**
   * Listen to a game event by name.
   */
  public onGameEvent(eventName: string | number, cb: (eventData: any) => any) {
    this.on(`__game_event_${eventName}`, cb)
  }
  /**
   * Stop listening to a game event.
   */
  public offGameEvent(eventName: string | number, cb: (eventData: any) => any) {
    this.off(`__game_event_${eventName}`, cb)
  }
  /**
   * Listen once to a game event.
   */
  public onceGameEvent(eventName: string | number, cb: (eventData: any) => any) {
    this.once(`__game_event_${eventName}`, cb)
  }

  /**
   * Send a generic game message to the server.
   */
  public sendGameMessage(message: { type: string | number, data: any }, reliable?: boolean, ordered?: boolean) {
    const payload = [PROTOCOLS.CLIENT_TO_SERVER_MESSAGE, message.type, message.data];
    this.connector.send(payload as ProtocolArray, reliable, ordered)
  }

  /**
   * Send a system-targeted message to the server.
   */
  public sendSystemMessage(message: { type: string | number, data: any, to: string | number}, reliable?: boolean, ordered?: boolean) {
    const payload = [PROTOCOLS.CLIENT_TO_SERVER_MESSAGE, message.type, message.data, message.to];
    this.connector.send(payload as ProtocolArray, reliable, ordered)
  }

  /**
   * Stop the current game and disconnect.
   */
  public async clearGame() {
    this.forceClosed = true;
    this.teardownUnloadHandler();
    this.process.clear();
    this.signalDeliberateLeave(this.connector);
    // Same WebRTC-teardown-safety flush as close() (see flushBeforeLeave()'s
    // doc + close() below): without it, peerConnection.destroy() can abort the
    // SCTP transport with the CLIENT_LEAVING frame still buffered, dropping the
    // signal and falling back to the 60s grace path. Best-effort/capped, so
    // this can never hang clearGame().
    try { await this.connector?.flushBeforeLeave?.(); } catch (e) { /* best-effort */ }
    this.connector?.disconnect();
  }

  /**
   * Tell the server this teardown is DELIBERATE (a user-initiated
   * close()/clearGame(), not a dropped socket) so the connector marks the
   * impending disconnect as intentional: the area evicts this client
   * immediately (fires onClientLeft) and the gate purges its auth now, instead
   * of the grace-hold/detach path — which holds a seat ~60s waiting on a
   * reconnect that a deliberate leave will never make. Sent over the STILL-live
   * connector BEFORE it is disconnected, so the ordered/reliable frame reaches
   * the connector ahead of the socket-close event. Best-effort: a dead or
   * already-closing socket must never make close()/clearGame() throw — a send
   * failure is swallowed and the server simply falls back to the abnormal-drop
   * grace path (the frame is a hint, never load-bearing for correctness).
   */
  private signalDeliberateLeave(connector?: BaseClientConnector) {
    try {
      connector?.send([PROTOCOLS.CLIENT_LEAVING], true, true);
    } catch (e) {
      // best-effort: the socket may already be gone
    }
  }

  /**
   * A browser page teardown — reload, tab close, navigation — destroys this
   * Client without anyone calling close()/clearGame(), so the connector's socket
   * simply dies and the server would treat it as an ABNORMAL drop: grace-holding
   * the seat ~60s and leaving a ghost membership other clients still see (e.g. a
   * duplicate entry in a player list after a reload). A real page unload IS a
   * deliberate leave, so fire the CLIENT_LEAVING hint synchronously — the only
   * thing an unload handler can reliably do (no awaiting a flush) — so the area
   * evicts immediately and the gate purges auth now. This does NOT undo the
   * failover grace path: a genuine transport drop (connector crash, network
   * blip) fires no unload event, so the client's reconnect driver still runs and
   * the server still grace-holds. Best-effort like signalDeliberateLeave: an
   * already-dead socket must never make unload throw, and the server falls back
   * to the grace path if the frame never lands. No-op under node/mock/test.
   */
  private installUnloadHandler() {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') return;
    this.unloadHandler = () => {
      if (this.forceClosed || !this.connector) return;
      this.forceClosed = true;               // page is dying — never try to reconnect
      this.signalDeliberateLeave(this.connector);
    };
    window.addEventListener('pagehide', this.unloadHandler);
    window.addEventListener('beforeunload', this.unloadHandler);
  }

  private teardownUnloadHandler() {
    if (!this.unloadHandler || typeof window === 'undefined') return;
    window.removeEventListener('pagehide', this.unloadHandler);
    window.removeEventListener('beforeunload', this.unloadHandler);
    this.unloadHandler = undefined;
  }

  /**
   * Register a client game file.
   */
  public addGameFile(file: ClientGameFile) {
    this.gameFiles[file.type] = file;
  }
  /**
   * Register a client area file.
   */
  public addAreaFile(file: ClientAreaFile) {
    this.areaFiles[file.type] = file;
  }

  /**
   * Authenticate this client with the Gate server.
   */
  public async authenticate(data: any) : Promise<AuthenticatedData> {
    if(this.indexedDb) { await this.indexedDb.awaitInitialized(); }
    // console.log('makijng gate post request', `${this.config.gateServerURL}/${ENDPOINTS.POST.GOTTI_AUTHENTICATE}`, data)
    const auth = await httpPostAsync(`${this.config.gateServerURL}/${ENDPOINTS.POST.GOTTI_AUTHENTICATE}`, '', data) as AuthenticatedData
    // console.log('auth data', auth)
    this.privateId = auth.privateId;
    this.publicId = auth.publicId;
    this.auth.privateId = auth.privateId;
    this.auth.publicId = auth.publicId;
    this.auth.id = auth.publicId;
    this.auth.data = auth.auth;
    return auth;
  }

  /**
   * Request a peer connection.
   */
  public async addPeer(playerId: string | number, opts: any) {
    return new Promise((resolve, reject) => {
      this.connector.requestPeerConnection(playerId, '', opts, (err, _opts) => {
        if(err) {
          return reject(err);
        }
        return resolve(_opts);
      })
    })
  }


  /**
   * Remove a peer connection.
   */
  public async removePeer(playerId: string | number) {
    this.connector.stopPeerConnection(playerId)
  }


  /**
   * Fetch available games from the Gate server.
   */
  public async getGames(options?: any) : Promise<GameList> {
    if(!this.auth) {
      throw new Error(`Must run client.authenticate first`)
    }
    const data = await httpPostAsync(`${this.config.gateServerURL}/${ENDPOINTS.POST.GOTTI_GAMES_LIST}`, this.privateId, options) as GameList;
    return data;
  }

  /**
   * Start a local/offline game session.
   */
  public async startGame(gameType: GameType | ClientGameFile, gameData?: any) {
    if(this.indexedDb) { await this.indexedDb.awaitInitialized(); }
    if(this.process.started) {
      this.process.stopLoop();
    }
    let gameTypeKey: GameType;
    //console.log({ gameType, gameData, gameFiles: this.gameFiles });
    if(typeof gameType === "object") {
      this.addGameFile(gameType);
      gameTypeKey = gameType.type;
    } else {
      gameTypeKey = gameType;
    }
    await this.process.startGame(this.gameFiles[gameTypeKey], gameData);
    return true;
  }

  // Winds up triggering the area file onJoinRequest; if it returns truthy the
  // join is valid and that return value is the `options` property of the
  // resolved promise.
  // EXCEPT when that area is already HOLDING A SEAT for us (our connector
  // dropped and its grace window has not elapsed): the area rebinds us without
  // calling onJoinRequest at all, and `options` resolves to the seat's own
  // options with `rejoin: true` set. An area cannot refuse a client it is
  // already holding.
  /**
   * Join a specific, existing area by id. Dynamic areas are NOT named by the
   * client — the server decides which dynamic area a client lands in (via the
   * game's `getInitialAreas`, which has the client's auth) and the framework
   * create-or-joins it; the client never picks an arbitrary area/key.
   */
  public async joinArea(areaId: string, options?: any) : Promise<{ area: JoinedAreaData, options: any }> {
    return new Promise((resolve, reject) => {
      this.once(`joined-area-${areaId}`, ({ error, area, options }: { error?: string, area?: JoinedAreaData, options?: any }) => {
        if(error) {
          reject(new Error(String(error)))
        } else {
          resolve({ area, options });
        }
      });
      this.connector.joinArea(areaId, options);
    });
  }

  /**
   * Join a game via the Gate server.
   */
  public async joinGame(options?: any, initialAreaOptions?: any, suppressJoinAreas = false) : Promise<{ game: JoinedGameInfo, joiningAreaIds?: Array<string> }> {
      // console.log('IN JOIN GMAE 1')
      if(this.indexedDb) { await this.indexedDb.awaitInitialized(); }
      if(this.process.started) {
        this.process.stopLoop();
      }
      // console.log('IN JOIN GMAE 2', this.privateId, options)
      const game : JoinedGameInfo = await httpPostAsync(`${this.config.gateServerURL}/${ENDPOINTS.POST.GOTTI_JOIN_GAME}`, this.privateId, options) as JoinedGameInfo;
      // console.log('JOINED GAME', { game })
      // console.log('IN JOIN GMAE 3')
      if(!game) throw new Error(`Failed to join game when sent opts ${options}. Server response was null`);
      // The gate may have RESUMED a held session for our verified identity — in
      // which case our in-game id is that session's publicId, not the one
      // authenticate() minted. Adopt it HERE, before handleConnect below is
      // called with `publicId: this.publicId`: the connector validates the
      // publicId we send against the reserved seat's (onValidateClient), so a
      // resumed publicId that never reaches us fails every connection.
      if(game.publicId !== undefined && game.publicId !== null) {
        this.publicId = game.publicId;
        this.auth.publicId = game.publicId;
        this.auth.id = game.publicId;
      }
      // Stash what the auto-reconnect driver needs to re-run this exact join.
      // These are DIFFERENT shapes and must NOT be collapsed into one field:
      // lastGameJoinBody goes back to the GATE (game-selection, e.g. a
      // `{ gameId }`-shaped body), lastAreaJoinOptions goes to
      // pingForFirstAreas (area-selection, e.g. an areaId array) — a caller
      // that passes both would otherwise send the area shape to the gate.
      this.lastGameJoinBody = options;
      this.lastAreaJoinOptions = initialAreaOptions || options;
      this.joinedGameId = game.gameId;
      // console.log('trying to connect')
      try {
        if(!this.connector || this.connector.connectorHost !== game.host || this.connector.connectorPort !== game.port) {
          this.connector?.disconnect();
          delete this.connector
          this.connector = this.makeConnector(game.connectorType);
          this.wireConnectionLost();
          await this.connector.handleConnect({
            ...game,
            privateId: this.privateId,
            publicId: this.publicId,
            listenHost: this.config.udpHost,
            listenPort: this.config.udpPort,
          });
        }
      } catch (err) {
        console.error('Error when connecting...', err);
        throw err;
      }

      this.process.initialize(this.connector);
      //console.log('JOINED GAME', { game, suppressJoinAreas });
      await this.startGame(game.gameType, game.gameData);
      const joinObject : { game: JoinedGameInfo, joiningAreaIds?: Array<string> } = { game };
      if(!suppressJoinAreas) {
          //console.log('JOIN INITIAL AREAS...');
          joinObject.joiningAreaIds = await this.joinInitialAreas(initialAreaOptions || options);
          //console.log('JOINED INITIAL AREAS', { joiningAreaIds: joinObject.joiningAreaIds });
      }
      this.emit('joined-game');
      return joinObject;
      // this.process.startGame(this.gameFiles['game']);
  }

  /** Build a fresh transport for the connector type the gate handed back.
   *  Shared by joinGame and the reconnect driver so there is exactly one
   *  connector-construction switch. */
  private makeConnector(type: JoinedGameInfo['connectorType']): BaseClientConnector {
    if (type === 'udp') return new ClientConnectorUDP(this);
    if (type === 'webRtc') return new ClientConnectorWebRTC(this);
    if (type === 'websocket') return new ClientConnectorWebSocket(this);
    throw new Error(`Unknown connector type: ${type}`);
  }

  /** Wire the current connector's unexpected-death callback to the reconnect
   *  driver. Called after EVERY connector construction (joinGame + reconnect). */
  private wireConnectionLost() {
    if (this.connector) {
      this.connector.onConnectionLost = () => this.handleConnectionLost();
    }
  }

  /** Resolve the registered area file for a joined area (by its live type). */
  private areaFileFor(areaId: string): ClientAreaFile | undefined {
    const meta = this.process.joinedAreasById[areaId];
    if (!meta) return undefined;
    return this.areaFiles[meta.type as string];
  }

  /**
   * The connector died unexpectedly. Re-run the gate→connector→join flow with
   * exponential backoff; the server routes us back into the areas it is holding
   * for us (a list it derives itself — we assert none). A deliberate teardown
   * (close/clearGame) sets `forceClosed` and clears `onConnectionLost`, so this
   * never runs for those. Re-entrancy is guarded by `reconnecting`. Exhausting
   * every attempt tears the still-pending areas down and resets the game-level
   * "started" state so a fresh `joinGame()` can run again on this SAME
   * instance — no need to construct a new `Client`.
   */
  private async handleConnectionLost() {
    if (this.forceClosed || this.reconnecting) return;
    this.reconnecting = true;
    this.emit('connection-lost');

    this.pendingRejoinAreaIds = new Set(Object.keys(this.process.joinedAreasById));
    for (const areaId of this.pendingRejoinAreaIds) {
      this.areaFileFor(areaId)?.onReconnectStart?.(this, { areaId });
    }

    // Tear down the dead transport (peers/timeouts) — its onConnectionLost was
    // already cleared by disconnect(), so this won't re-enter here.
    this.connector?.disconnect();
    delete this.connector;

    while (this.reconnectAttempt < CLIENT_RECONNECT_MAX_ATTEMPTS) {
      this.emit('reconnecting', ++this.reconnectAttempt);
      try {
        await this.rejoinGame();
        this.reconnectAttempt = 0;
        this.reconnecting = false;
        this.emit('reconnected');
        return;
      } catch (e) {
        const delay = Math.min(
          CLIENT_RECONNECT_BACKOFF_MS * 2 ** (this.reconnectAttempt - 1),
          CLIENT_RECONNECT_BACKOFF_CAP_MS,
        );
        await new Promise(r => setTimeout(r, delay));
        if (this.forceClosed) break;
      }
    }

    this.reconnecting = false;
    // Reset so the NEXT connection-loss episode starts at attempt 1 again —
    // otherwise a client that survives past a gave-up (a fresh joinGame()
    // reconnected it) would find reconnectAttempt already pinned at the max
    // and give up on its very first future attempt with 0 real tries.
    this.reconnectAttempt = 0;

    // A deliberate close() during the backoff wait falls out of the loop the
    // same way exhausting every attempt does, but it is NOT a reconnect
    // failure — close() already owns tearing itself down. Stay silent (no
    // events, no per-area hooks) and just drop this episode's bookkeeping.
    if (this.forceClosed) {
      this.pendingRejoinAreaIds = undefined;
      return;
    }

    // Gave up: tear down every area still awaiting a rejoin through the same
    // path a rejected/gone rejoin uses (fires onReconnectFailure, then leaves
    // the area so its client-side state doesn't leak — see failRejoinArea),
    // clear the episode's bookkeeping, and reset the game-level "started"
    // flags so a fresh joinGame() doesn't immediately hit "Already started
    // game" on this same instance. Only rewind process state that was
    // actually started — handleConnectionLost can in theory fire in the
    // narrow window before the first joinGame()'s startGame() call lands.
    for (const areaId of Array.from(this.pendingRejoinAreaIds ?? [])) {
      this.failRejoinArea(areaId, 'gave-up');
    }
    this.pendingRejoinAreaIds = undefined;
    if (this.process.started) {
      this.process.stopGame();
    }
    this.emit('reconnect-failed');
  }

  /**
   * One reconnect attempt: re-reserve a seat at the gate (SAME game-selection
   * body — the gate matches us by verified auth identity and derives our held
   * areas), build a fresh connector, then re-run the normal `pingForFirstAreas`
   * path, which the server answers from that gate-derived list. NO startGame —
   * the process/systems/areas stay live; only the transport is replaced.
   */
  private async rejoinGame() {
    // A previous attempt within this SAME episode may have built a connector
    // and then failed later (gate ok, but connect/rejoin-wait failed) — that
    // dead connector was never disconnected. Clear it before building a new
    // one: otherwise it leaks listeners/timers, and a late event on it could
    // re-trigger handleConnectionLost after this (or a later) attempt succeeds.
    this.connector?.disconnect();
    delete this.connector;

    const game = await httpPostAsync(
      `${this.config.gateServerURL}/${ENDPOINTS.POST.GOTTI_JOIN_GAME}`,
      this.privateId,
      this.lastGameJoinBody,
    ) as JoinedGameInfo;
    if (!game) throw new Error(`Reconnect failed: gate returned null for game ${this.joinedGameId}`);

    // ALWAYS build a fresh connector — the dead socket must not be reused.
    this.connector = this.makeConnector(game.connectorType);
    this.wireConnectionLost();
    await this.connector.handleConnect({
      ...game,
      privateId: this.privateId,
      publicId: this.publicId,
      listenHost: this.config.udpHost,
      listenPort: this.config.udpPort,
    });
    this.process.initialize(this.connector);

    // Areas are restored from SERVER state: the gate matched our verified auth
    // identity to the seats it is holding for us and put that list on the seat.
    // We deliberately assert nothing here — a client naming its own areas is
    // exactly the hole this replaced. We replay only our AREA options, so the
    // rejoined areas see the same join options the original join used.
    // (ConnectorServer merges the SEAT's own options — including the server-set
    // `rejoin` flag — on top of whatever we send, whatever its shape, so this
    // object's contents are ours alone to decide. An area holding our seat reads
    // that flag in `onClientConnected`, never in `onJoinRequest`: it rebinds us
    // without consulting that hook. `onJoinRequest` sees the flag only where it
    // still runs — a FIRST join into an area that is not holding a seat for us.)
    this.connector.pingForFirstAreas({ ...(this.lastAreaJoinOptions || {}) });

    await new Promise<void>((resolve, reject) => {
      // Register with on()+off() rather than once(): a once() listener only
      // detaches itself when it FIRES, so a timeout would abandon it. A leaked
      // 'joined-first-areas' listener is not merely wasteful — EventEmitter.emit
      // iterates its listener array live while a self-removing listener splices
      // it, so a stale listener firing first SKIPS the next attempt's live
      // listener, and a healthy server can never complete a later attempt. So we
      // remove `handler` in BOTH the fire path and the timeout path.
      const handler = ({ error, areaIds }: { error?: any, areaIds?: string[] }) => {
        this.off('joined-first-areas', handler);
        clearTimeout(timer);
        if (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
          return;
        }
        // Any area we asked to rejoin but the server did NOT route us back into
        // no longer exists (Task 4's master filter dropped it). Areas present in
        // the result clear themselves when their CLIENT_JOINED_AREA lands.
        const survived = new Set((areaIds || []).map(String));
        for (const areaId of Array.from(this.pendingRejoinAreaIds ?? [])) {
          if (!survived.has(String(areaId))) {
            this.failRejoinArea(areaId, 'area-gone');
          }
        }
        resolve();
      };
      const timer = setTimeout(() => {
        this.off('joined-first-areas', handler);
        reject(new Error('Reconnect timed out waiting for joined-first-areas'));
      }, this.config.requestTimeout || 30000);
      this.on('joined-first-areas', handler);
    });
  }

  /** Fire onReconnectFailure for an area that could not be rejoined and tear
   *  down its client-side state so it does not leak. `reason` is `'rejected'`
   *  or `'area-gone'` for a single area during a live rejoin attempt, or
   *  `'gave-up'` when the WHOLE reconnect episode exhausted its attempts
   *  (see handleConnectionLost's post-loop cleanup). */
  private failRejoinArea(areaId: string, reason: 'rejected' | 'area-gone' | 'gave-up') {
    this.pendingRejoinAreaIds?.delete(areaId);
    this.areaFileFor(areaId)?.onReconnectFailure?.(this, { areaId, reason });
    if (this.process.joinedAreasById[areaId]) {
      this.handleLeftArea(areaId, LEAVE_AREA_REASONS.DISCONNECTED);
    }
  }

  /**
   * Join initial areas announced by the Gate.
   */
  public async joinInitialAreas(options?: any) : Promise<string[]> {
      this.connector.pingForFirstAreas(options);
      return new Promise((resolve, reject) => {
          this.once('joined-first-areas', ({ error, areaIds }) => {
            //console.log('GOT JOINED FIRST AREAS EVENT', { error, areaIds });
              if(error) {
                  reject(error)
              } else {
                  resolve(areaIds);
              }
          })
      });
  }

  private async handleJoinedAreaSuccess(
      areaId: string,
      areaType: string, 
      areaData: any,
      areaState: any,
      options: any, 
      leavesOtherAreas: boolean, 
  ) {
    /*
    console.log('handleJoinedAreaSuccess', {
      areaId, 
      areaType, 
      areaData, 
      areaState, 
      options, 
      leavesOtherAreas
    })*/
    // in testing locally the handleJoinedArea will get triggered before game finishes joining if you dont suppress the ping.
    const joinedAreaData : JoinedAreaData = { id: areaId, type: areaType, data: areaData, state: areaState };

    // Idempotent rejoin: this area is ALREADY joined (its file + systems are
    // live from the original join and never lapsed through the reconnect). Do
    // NOT re-init the area file (would double-register systems) and do NOT fire
    // onJoined / onJoinedArea — membership never lapsed. Only refresh cached
    // data/state (the authoritative AREA_STATE_SYNC repopulates live state) and
    // surface the reconnect-success hook.
    if (this.process.joinedAreasById[areaId]) {
      this.process.rejoinArea(areaId, areaData, areaState);
      this.pendingRejoinAreaIds?.delete(areaId);
      this.areaFileFor(areaId)?.onReconnectSuccess?.(this, { area: joinedAreaData, options });
      return;
    }

    const areasToLeave = [];
    if(leavesOtherAreas) {
        areasToLeave.push(...Object.keys(this.process.joinedAreasById));
        // Mark them BEFORE initAreaFile: the outgoing areas stay joined until
        // the bottom of this method (so a system living in both areas keeps its
        // ref count and is never destroyed mid-switch), but they must stop being
        // dispatch targets right now — otherwise every send from onStart or
        // onJoinedArea sees two joined areas and throws as ambiguous.
        this.process.markAreasDeparting(areasToLeave);
    }
    const areaFile = this.areaFiles[areaType];
    if(areaFile) {
      this.initialzingAreas[areaId] = true;
      await this.process.initAreaFile(areaFile, areaId, joinedAreaData);
      this.onFinishedInitializingAreaListeners[areaId]?.forEach(cb => cb());
      delete this.onFinishedInitializingAreaListeners[areaId];
      delete this.initialzingAreas[areaId];
      areaFile.onJoined?.(this, { area: joinedAreaData, options: options });
    } else {
      this.process.initAreaData(areaId, joinedAreaData);
    }
    //console.log('emitting', `joined-area-${areaId}`);
    this.emit(`joined-area-${areaId}`, { area: joinedAreaData, options: options });
    this.emit('joined-area', {
        areaId,
        area: joinedAreaData,
        options: options,
    })
    this.joinedAreaCbs.forEach(cb => cb(joinedAreaData, options));
    this.process.systemArray.forEach(s => {
    // console.log('on joined s', s);
      s.onJoinedArea?.(joinedAreaData, options);
    });
    if(areasToLeave.length) {
      areasToLeave.forEach(a => {
        this.handleLeftArea(a, LEAVE_AREA_REASONS.SWITCHED_AREA, joinedAreaData);
      });
    }
  }
  
  //todo handle joining, then leaving, then joining AGAIN
  public async handleJoinedArea(
      areaId: string,
      areaType: string, 
      areaData: any,
      areaState: any,
      options: any, 
      leavesOtherAreas: boolean, 
      error?: string
  ) {
    //console.log('handleJoinedArea', areaId, areaType, areaData, areaState, options, leavesOtherAreas, error);
      // console.log('areaFile', areaId, this.areaFiles);
      if(!error) {
        await this.handleJoinedAreaSuccess(
          areaId,
          areaType, 
          areaData,
          areaState,
          options, 
          leavesOtherAreas,
        )
      } else {
        // A join for an area we are still trying to rejoin came back as an
        // ERROR: surface the per-area reconnect failure and tear the area down
        // so its client state doesn't leak. This is NOT the automatic reconnect
        // failing — that path (GET_FIRST_AREAS) never answers per-area with an
        // error, and an area holding our seat rebinds unconditionally without
        // consulting its onJoinRequest. The only remaining producer is our OWN
        // joinArea() call: ConnectorServer.handleRequestJoinArea threw (in
        // practice: that area is not connected to this connector) and the catch
        // relayed the message in the error slot.
        if (this.pendingRejoinAreaIds?.has(areaId)) {
          this.failRejoinArea(areaId, 'rejected');
        }
        this.emit(`joined-area-${areaId}`, { error });
        this.emit('joined-area', {
          areaId,
          error,
        })
      }
  }

  public onJoinedArea(cb: (area: JoinedAreaData, options: any) => any) {
    this.joinedAreaCbs.push(cb);
  }
  public offJoinedArea(cb: (area: JoinedAreaData, options: any) => any) {
    removeFromArray(this.joinedAreaCbs, cb);
  }
  public onceJoinedArea(cb: (area: JoinedAreaData, options: any) => any) {
    const _cb = (area: JoinedAreaData, options: any) => {
      cb(area, options);
      this.offJoinedArea(_cb);
    }
    this.onJoinedArea(_cb);
  }
  
  public onLeftArea(cb: (v: { areaId: string | number, reason: LEAVE_AREA_REASONS, options?: any }) => void) {
    this.leftAreaCbs.push(cb);
  }
  public offLeftArea(cb: (v: { areaId: string | number, reason: LEAVE_AREA_REASONS, options?: any }) => void) {
    removeFromArray(this.leftAreaCbs, cb);
  }
  public onceLeftArea(cb: (v: { areaId: string | number, reason: LEAVE_AREA_REASONS, options?: any }) => void) {
    const _cb = (v: { areaId: string | number, reason: LEAVE_AREA_REASONS, options?: any }) => {
      cb(v);
      this.offLeftArea(_cb);
    }
    this.onLeftArea(_cb);
  }
  public leaveArea(areaFile?: ClientAreaFile, areaId?: string, options?: any, reason?: LEAVE_AREA_REASONS, ) {
    if(areaFile) {
      this.process.denitAreaFile(areaFile, areaId);
    } else {
      this.process.removeAreaData(areaId);
    }
    this.process.systemArray.forEach(s => {
      if(areaId in s.states) {
        const state = s.states[areaId];
        delete s.states[areaId];
        s.onAreaSystemStateRemoved?.(areaId, state);
      }
      s.onLeftArea?.(areaId, reason, options);
    });
    areaFile?.onLeft?.(this, { areaId, reason, options})
    this.leftAreaCbs.forEach(cb => cb({ areaId, reason, options }))
  }
  
  public handleAreaStateSync(areaId: string, newState: any) {
    const previousState = this.process.$areaState[areaId];
    this.process.$areaState[areaId] = newState;
    this.process.systemArray.forEach(s => {
        s.onUpdatedAreaState?.(areaId, newState, previousState);
    });
    const areaFile = this.areaFiles[this.process.joinedAreasById[areaId].type];
    areaFile?.onUpdatedState?.(this, { areaId, newState, previousState });
  }

  public handleRawAreaMessage(data: ArrayBuffer) {
    for (let i = 0; i < this.process.rawAreaMessageSystems.length; i++) {
      this.process.rawAreaMessageSystems[i].onRawAreaMessage!(data);
    }
    const deliveredAreaTypes = new Set<string>();
    Object.values(this.process.joinedAreasById).forEach(areaMeta => {
      if(!areaMeta) {
        return;
      }
      const areaTypeKey = String(areaMeta.type);
      if(deliveredAreaTypes.has(areaTypeKey)) {
        return;
      }
      deliveredAreaTypes.add(areaTypeKey);
      this.areaFiles[areaTypeKey]?.onRawAreaMessage?.(this, data);
    });
  }

  public handleLeaveAllAreas(options?: any) {
    const keys = Object.keys(this.process.joinedAreasById);
    keys.forEach(k => {
      this.handleLeftArea(k, options);
    })
  }

  public handleLeftArea(areaId: string, reason: LEAVE_AREA_REASONS, options?: any) {
    //console.log('handleLeftArea', { areaId, reason, options });
    const d = this.process.joinedAreasById[areaId];
    const file = this.areaFiles[d.type];
    const onLeave = this.leaveArea.bind(this, file, areaId, options, reason) ;
    
    // in case we leave before it finishes initializing... wait till it finishes before leaving
    if(this.initialzingAreas[areaId]) {
      if(this.onFinishedInitializingAreaListeners[areaId]) {
        this.onFinishedInitializingAreaListeners[areaId].push(onLeave);
      } else {
        this.onFinishedInitializingAreaListeners[areaId] = [onLeave];
      }
    } else {
      onLeave();
    }
  }

  public handlePeerConnectionStarted(playerId: string, opts: any) {
    this.peerStartedCbs.forEach(cb => cb(playerId, opts));
  }
  public handlePeerConnectionEnded(playerId: string) {
    this.peerEndedCbs.forEach(cb => cb(playerId));
  }
  public onPeerConnectionRequest(cb: (
      playerId: string | number, opts: any,
  ) => any) {
    this.handlePeerConnectionRequest = cb
  }
  public offPeerConnectionRequest() {
    delete this.handlePeerConnectionRequest
  }
  
  public onPeerConnectionStarted(cb: (
      playerId: string | number, opts: any,
  ) => void) {
    this.peerStartedCbs.push(cb);
  }
  
  public offPeerConnectionStarted(cb: (
      playerId: string | number, opts: any,
  ) => void) {
    removeFromArray(this.peerStartedCbs, cb);
  }

  public onPeerConnectionEnded(cb: (
      playerId: string | number,
  ) => void) {
    this.peerEndedCbs.push(cb);
  }

  public offPeerConnectionEnded(cb: (
      playerId: string, opts: any,
  ) => Promise<any>) {
    removeFromArray(this.peerEndedCbs, cb);
  }

  public onPeerMessage(cb: (
      playerId: string | number, data: { type: string | number, data: any },
  ) => void) {
    this.handlePeerMessage = cb;
  }


  public offPeerMessage() {
    delete this.handlePeerMessage;
  }
  public onAreaRequest(cb: (areaId: string, options: ReceivedMessage) => any | void | Promise<void | any>) {
    this.handleAreaRequest = cb;
  }
  public offAreaRequest() {
    delete this.handleAreaRequest;
  }

  public async close() {
    this.forceClosed = true;
    this.teardownUnloadHandler();
    const c = this.connector;
    delete this.connector;
    this.signalDeliberateLeave(c);
    // Give the deliberate-leave frame a chance to actually reach the wire before
    // tearing the transport down. Websocket's close() already flushes queued
    // data, but WebRTC's destroy() would abort the SCTP transport with the
    // reliable frame still buffered — dropping the signal so a clean quit
    // silently falls back to the 60s grace path. flushBeforeLeave() is a capped,
    // best-effort no-op for transports that don't need it.
    try { await c?.flushBeforeLeave?.(); } catch (e) { /* best-effort */ }
    c?.disconnect();
  }
}
